import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Checkbox,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FolderIcon from '@mui/icons-material/Folder';
import { SourceType, ContentType, SourceSelection } from './TransferManager';
import { moodleApi, MoodleSource, MoodleCourse, CompetencyFramework, LearningPathTemplate } from '../services/moodle-api';
import { contentApi, FileItem, basePrefixApi, versionApi, VersionMetadata } from '../services/api';
import { CMI5ActivitiesRow } from './CMI5ActivitiesRow';

interface SourcePaneProps {
  currentEnvironment: string;
  onSelectionChange: (selection: SourceSelection | null) => void;
  onItemsChange?: (items: any[]) => void; // Expose all loaded items for comparison
  destinationItems?: any[]; // Items from destination to compare against
}

export const SourcePane: React.FC<SourcePaneProps> = ({ currentEnvironment, onSelectionChange, onItemsChange, destinationItems = [] }) => {
  const [sourceType, setSourceType] = useState<SourceType>('s3');
  const [contentType, setContentType] = useState<ContentType>('courses');
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'updated'>('all');
  const [renderKey, setRenderKey] = useState(0); // Force re-render when destination changes
  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    id: true,
    shortname: true,
    category: true,
    size: true,
    date: true,
  });
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

  // Moodle-specific state
  const [moodleSources, setMoodleSources] = useState<MoodleSource[]>([]);
  const [selectedMoodleSource, setSelectedMoodleSource] = useState<string>('');
  const [moodleCourses, setMoodleCourses] = useState<MoodleCourse[]>([]);
  const [moodleFrameworks, setMoodleFrameworks] = useState<CompetencyFramework[]>([]);
  const [moodleTemplates, setMoodleTemplates] = useState<LearningPathTemplate[]>([]);
  const [moodleCategories, setMoodleCategories] = useState<Array<{ id: number; name: string; parent: number; path: string }>>([]);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<string[]>([]);

  // S3-specific state
  const [s3Environments, setS3Environments] = useState<Array<{ id: string; name: string; prefix: string; bucketName: string }>>([]);
  const [selectedS3Environment, setSelectedS3Environment] = useState<string>('');
  const [s3Files, setS3Files] = useState<FileItem[]>([]);
  const [s3Categories, setS3Categories] = useState<Array<{ id: number; name: string; parent: number; path: string; sourceFile?: string }>>([]);
  const [s3CategoryFiles, setS3CategoryFiles] = useState<string[]>([]); // Track CSV file keys

  // Common state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Version selection state
  const [selectedVersions, setSelectedVersions] = useState<Record<string, string>>({});
  const [selectedCmi5ActivityVersions, setSelectedCmi5ActivityVersions] = useState<Record<string, Record<string, string>>>({});

  // Version selection dialog state
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [versionDialogFile, setVersionDialogFile] = useState<FileItem | null>(null);
  const [versionDialogVersions, setVersionDialogVersions] = useState<VersionMetadata[]>([]);
  const [versionDialogLoading, setVersionDialogLoading] = useState(false);

  // Force re-render when destinationItems changes to update status chips
  useEffect(() => {
    setRenderKey(prev => prev + 1);
  }, [destinationItems, destinationItems.length]);

  // Load Moodle sources on mount
  useEffect(() => {
    loadMoodleSources();
    loadS3Environments();
  }, []);

  // Update selected S3 environment when currentEnvironment changes
  useEffect(() => {
    if (sourceType === 's3' && currentEnvironment && s3Environments.length > 0) {
      // Find all environments that match the currentEnvironment prefix
      const matchingEnvs = s3Environments.filter(env => env.prefix === currentEnvironment);
      // Only auto-select if there's exactly one match (avoid ambiguity with same prefix in different buckets)
      if (matchingEnvs.length === 1) {
        setSelectedS3Environment(matchingEnvs[0].id);
      }
      // If multiple matches, don't auto-select - let user choose
    }
  }, [currentEnvironment, sourceType, s3Environments]);

  // Load content when source/content type changes
  useEffect(() => {
    setSelectedItems(new Set());
    setError(null);

    if (sourceType === 'moodle' && selectedMoodleSource) {
      loadMoodleContent();
    } else if (sourceType === 's3' && selectedS3Environment) {
      loadS3Content();
    }
  }, [sourceType, selectedMoodleSource, selectedS3Environment, contentType]);

  // Notify parent of selection changes
  useEffect(() => {
    if (selectedItems.size === 0) {
      onSelectionChange(null);
      return;
    }

    const instanceId = sourceType === 'moodle' ? selectedMoodleSource : selectedS3Environment;
    if (!instanceId) {
      onSelectionChange(null);
      return;
    }

    // Get the instance name
    const instanceName = sourceType === 'moodle'
      ? moodleSources.find(s => s.id === selectedMoodleSource)?.name
      : s3Environments.find(env => env.id === selectedS3Environment)?.name;

    // For S3 categories, map selected category IDs to their source CSV file keys
    let itemsToSend = Array.from(selectedItems);
    let displayCount: number | undefined = undefined;

    if (sourceType === 's3' && contentType === 'categories' && s3Categories.length > 0) {
      // Get unique CSV file keys from selected categories
      const selectedCategoryIds = Array.from(selectedItems).map(id => parseInt(id));
      const fileKeys = new Set(
        s3Categories
          .filter(cat => selectedCategoryIds.includes(cat.id))
          .map(cat => cat.sourceFile)
          .filter((key): key is string => key !== undefined)
      );
      itemsToSend = Array.from(fileKeys);
      displayCount = selectedCategoryIds.length; // Show count of selected categories, not CSV files
      console.log('Mapped category IDs to file keys:', { selectedItems: Array.from(selectedItems), fileKeys: itemsToSend, displayCount });
    }

    const selection: any = {
      type: sourceType,
      instanceId,
      instanceName,
      contentType,
      selectedItems: itemsToSend,
      displayCount,
      selectedVersions,
      selectedCmi5ActivityVersions,
    };

    // Add environment prefix for S3 sources
    if (sourceType === 's3') {
      const env = s3Environments.find(e => e.id === selectedS3Environment);
      if (env) {
        selection.environmentPrefix = env.prefix;
      }
    }

    onSelectionChange(selection);
  }, [sourceType, selectedMoodleSource, selectedS3Environment, contentType, selectedItems, moodleSources, s3Environments, onSelectionChange, s3Categories, selectedVersions, selectedCmi5ActivityVersions]);

  const loadMoodleSources = async () => {
    try {
      const sources = await moodleApi.listSources();
      setMoodleSources(sources);
      if (sources.length > 0 && !selectedMoodleSource) {
        setSelectedMoodleSource(sources[0].id);
      }
    } catch (err) {
      console.error('Failed to load Moodle sources:', err);
    }
  };

  const loadS3Environments = async () => {
    try {
      const environments = await basePrefixApi.getEnvironmentsWithBuckets();
      setS3Environments(environments.map(env => ({
        id: env.environmentId,
        name: env.environmentName,
        prefix: env.prefix,
        bucketName: env.bucketName,
      })));
    } catch (err) {
      console.error('Failed to load S3 environments:', err);
    }
  };

  // Helper function to extract display name from metadata
  const getDisplayName = (file: FileItem): string => {
    if (!file.metadata) return file.name;

    // For courses: prefer courseFullname, then courseShortname
    if (contentType === 'courses') {
      const fullname = file.metadata['courseFullname'] || file.metadata['coursefullname'];
      if (fullname) return fullname;

      const shortname = file.metadata['courseShortname'] || file.metadata['courseshortname'];
      if (shortname) return shortname;
    }

    // For CMI5 activities: prefer cmi5-activity-name
    if (contentType === 'cmi5_activities') {
      const activityName = file.metadata['cmi5-activity-name'];
      if (activityName) return activityName;
    }

    // For other content: use any available name field
    const nameField = file.metadata['name'] || file.metadata['title'] || file.metadata['displayname'];
    if (nameField) return nameField;

    // Fallback to filename
    return file.name;
  };

  // Open version selection dialog for a file
  const handleOpenVersionDialog = async (file: FileItem, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row selection
    setVersionDialogFile(file);
    setVersionDialogOpen(true);
    setVersionDialogLoading(true);

    try {
      // Get folder name from contentType
      const folderMap: Record<ContentType, string> = {
        courses: 'courses',
        competency_frameworks: 'competency_frameworks',
        learning_path_templates: 'learning_path_templates',
        categories: 'categories',
      };

      const versions = await versionApi.getVersionHistory(folderMap[contentType], file.name);
      setVersionDialogVersions(versions);
    } catch (err) {
      console.error('Failed to load version history:', err);
      setVersionDialogVersions([]);
    } finally {
      setVersionDialogLoading(false);
    }
  };

  // Handle version selection
  const handleSelectVersion = (version: string) => {
    if (versionDialogFile) {
      setSelectedVersions(prev => ({
        ...prev,
        [versionDialogFile.key]: version,
      }));
    }
    setVersionDialogOpen(false);
  };

  // Clear version selection for a file
  const handleClearVersion = (fileKey: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row selection
    setSelectedVersions(prev => {
      const newVersions = { ...prev };
      delete newVersions[fileKey];
      return newVersions;
    });
  };

  // Handle CMI5 activity version changes
  const handleActivityVersionChange = (courseKey: string, activityKey: string, version: string | null) => {
    setSelectedCmi5ActivityVersions(prev => {
      const newVersions = { ...prev };

      if (version === null) {
        // Remove the version selection
        if (newVersions[courseKey]) {
          delete newVersions[courseKey][activityKey];
          // If no more activities for this course, remove the course entry
          if (Object.keys(newVersions[courseKey]).length === 0) {
            delete newVersions[courseKey];
          }
        }
      } else {
        // Add or update the version selection
        if (!newVersions[courseKey]) {
          newVersions[courseKey] = {};
        }
        newVersions[courseKey][activityKey] = version;
      }

      return newVersions;
    });
  };

  const loadMoodleContent = async () => {
    if (!selectedMoodleSource) return;

    setLoading(true);
    setError(null);
    try {
      switch (contentType) {
        case 'courses':
          const courses = await moodleApi.listCourses(selectedMoodleSource);
          setMoodleCourses(courses);
          onItemsChange?.(courses);
          break;
        case 'competency_frameworks':
          const frameworks = await moodleApi.listCompetencyFrameworks(selectedMoodleSource);
          setMoodleFrameworks(frameworks);
          onItemsChange?.(frameworks);
          break;
        case 'learning_path_templates':
          const templates = await moodleApi.listLearningPathTemplates(selectedMoodleSource);
          setMoodleTemplates(templates);
          onItemsChange?.(templates);
          break;
        case 'categories':
          const categories = await moodleApi.listCategories(selectedMoodleSource);
          setMoodleCategories(categories);
          onItemsChange?.(categories);
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const loadS3Content = async () => {
    if (!selectedS3Environment) return;

    // Look up the environment to get the prefix
    const selectedEnv = s3Environments.find(env => env.id === selectedS3Environment);
    if (!selectedEnv) {
      setError('Selected environment not found');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Set the environment by ID (handles multiple buckets with same prefix)
      await basePrefixApi.setEnvironmentById(selectedS3Environment);

      // Load files from the folder
      const folderMap: Record<ContentType, string> = {
        courses: 'courses',
        competency_frameworks: 'competency_frameworks',
        learning_path_templates: 'learning_path_templates',
        categories: 'categories',
      };

      const files = await contentApi.getFolderContentsWithVersions(folderMap[contentType]);

      // Special handling for categories - parse CSV files
      if (contentType === 'categories' && files.length > 0) {
        try {
          // Parse all category CSV files and merge them
          const allCategories: Array<{ id: number; name: string; parent: number; path: string; sourceFile?: string }> = [];
          const csvFileKeys: string[] = [];

          for (const file of files) {
            if (file.key.endsWith('.csv')) {
              csvFileKeys.push(file.key);

              // Extract filename from key (remove folder prefix)
              const filename = file.key.split('/').pop() || file.key;

              // Fetch and parse the CSV file
              const csvContent = await contentApi.getFileContent('categories', filename);
              const lines = csvContent.split('\n').filter(line => line.trim());

              // Skip header line
              for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;

                // Parse CSV line: category,parent,idnumber,description,path
                const parts = line.split(',');
                if (parts.length >= 5) {
                  const categoryName = parts[0].replace(/^"|"$/g, '');
                  const parentName = parts[1].replace(/^"|"$/g, '');
                  const path = parts[4].replace(/^"|"$/g, '');

                  // Generate a unique ID based on the category name and path
                  const id = allCategories.length + 1;

                  // Find parent ID (will be resolved after all categories are loaded)
                  allCategories.push({
                    id,
                    name: categoryName,
                    parent: 0, // Will be resolved below
                    path: path || categoryName,
                    sourceFile: file.key, // Track which CSV file this came from
                  });
                }
              }
            }
          }

          // Resolve parent relationships based on path
          for (const cat of allCategories) {
            if (cat.path.includes(' / ')) {
              const pathParts = cat.path.split(' / ');
              const parentPath = pathParts.slice(0, -1).join(' / ');
              const parent = allCategories.find(c => c.path === parentPath);
              if (parent) {
                cat.parent = parent.id;
              }
            }
          }

          console.log('Parsed S3 categories:', allCategories);
          console.log('CSV file keys:', csvFileKeys);
          setS3Categories(allCategories);
          setS3CategoryFiles(csvFileKeys);
          onItemsChange?.(allCategories);
        } catch (parseError) {
          console.error('Error parsing category CSV:', parseError);
          setS3Files(files);
          onItemsChange?.(files);
        }
      } else {
        setS3Files(files);
        onItemsChange?.(files);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load S3 content');
    } finally {
      setLoading(false);
    }
  };

  // Determine if a source item is new or updated compared to destination
  const getItemStatus = (sourceItem: any): 'new' | 'updated' | 'duplicate' => {
    if (!destinationItems || destinationItems.length === 0) {
      return 'new'; // No destination data, treat as new
    }

    // Find matching item in destination
    const matchKey = contentType === 'courses' ? 'shortname' : 'idnumber';
    const destItem = destinationItems.find(item => {
      if (sourceType === 'moodle') {
        // Moodle to anything: match by shortname or idnumber
        // For S3 destinations, check metadata first
        const destValue = item.metadata?.courseshortname || item.metadata?.courseShortname || item[matchKey];
        return destValue === sourceItem[matchKey];
      } else {
        // S3 to anything: match by shortname from metadata first, then filename
        const srcShortname = sourceItem.metadata?.courseshortname || sourceItem.metadata?.courseShortname;
        if (srcShortname) {
          // If source has metadata shortname, match against destination shortname
          const destShortname = item.metadata?.courseshortname || item.metadata?.courseShortname || item[matchKey];
          if (destShortname === srcShortname) return true;
        }
        // Fallback to filename matching
        const srcFilename = sourceItem.key?.split('/').pop() || sourceItem.name;
        const destFilename = item.key?.split('/').pop() || item.name || item.shortname;
        return destFilename === srcFilename || srcFilename.includes(destFilename);
      }
    });

    if (!destItem) return 'new';

    // Compare timestamps
    // For S3 items, prefer metadata timemodified over lastModified (which is the upload time)
    const sourceTime = sourceItem.metadata?.coursetimemodified 
      ? parseInt(sourceItem.metadata.coursetimemodified, 10) 
      : (sourceItem.timemodified || sourceItem.lastModified);
    const destTime = destItem.metadata?.coursetimemodified 
      ? parseInt(destItem.metadata.coursetimemodified, 10)
      : (destItem.timemodified || destItem.lastModified);

    if (!sourceTime || !destTime) return 'duplicate'; // Can't determine, treat as duplicate

    const sourceMs = typeof sourceTime === 'number' ? sourceTime * 1000 : new Date(sourceTime).getTime();
    const destMs = typeof destTime === 'number' ? destTime * 1000 : new Date(destTime).getTime();

    // If source is newer, it's an update, otherwise it's a duplicate
    return sourceMs > destMs ? 'updated' : 'duplicate';
  };

  const handleToggleItem = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (sourceType === 'moodle') {
      let allItems: any[] = [];
      switch (contentType) {
        case 'courses':
          allItems = moodleCourses;
          break;
        case 'competency_frameworks':
          allItems = moodleFrameworks;
          break;
        case 'learning_path_templates':
          allItems = moodleTemplates;
          break;
        case 'categories':
          allItems = moodleCategories;
          break;
      }

      // Apply status filter to only select visible items
      const filteredItems = allItems.filter((item) => {
        if (statusFilter === 'all') return true;
        const status = getItemStatus(item);
        if (statusFilter === 'new') return status === 'new';
        if (statusFilter === 'updated') return status === 'updated';
        return true;
      });

      setSelectedItems(new Set(filteredItems.map(item => item.id.toString())));
    } else {
      // For S3 categories, use parsed categories instead of files
      if (contentType === 'categories' && s3Categories.length > 0) {
        const filteredCategories = s3Categories.filter((cat) => {
          if (statusFilter === 'all') return true;
          const status = getItemStatus(cat);
          if (statusFilter === 'new') return status === 'new';
          if (statusFilter === 'updated') return status === 'updated';
          return true;
        });

        setSelectedItems(new Set(filteredCategories.map(c => c.id.toString())));
      } else {
        // Apply status filter for S3 files
        const filteredFiles = s3Files.filter((file) => {
          if (statusFilter === 'all') return true;
          const status = getItemStatus(file);
          if (statusFilter === 'new') return status === 'new';
          if (statusFilter === 'updated') return status === 'updated';
          return true;
        });

        setSelectedItems(new Set(filteredFiles.map(f => f.key)));
      }
    }
  };

  const handleClearSelection = () => {
    setSelectedItems(new Set());
  };

  // Build hierarchical tree structure from flat category list
  const buildCategoryTree = (categories: Array<{ id: number; name: string; parent: number; path: string }>) => {
    const rootCategories = categories.filter(c => c.parent === 0);
    const categoryMap = new Map(categories.map(c => [c.id, c]));

    const buildChildren = (parentId: number): typeof categories => {
      return categories.filter(c => c.parent === parentId);
    };

    return { rootCategories, buildChildren, categoryMap };
  };

  // Render category tree recursively
  const renderCategoryTree = (category: { id: number; name: string; parent: number; path: string }, buildChildren: (id: number) => any[], level: number = 0) => {
    const children = buildChildren(category.id);
    const status = getItemStatus(category);
    const isSelected = selectedItems.has(category.id.toString());

    return (
      <TreeItem
        key={category.id}
        itemId={category.id.toString()}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <Checkbox
              size="small"
              checked={isSelected}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleItem(category.id.toString());
              }}
            />
            <FolderIcon sx={{ fontSize: '1rem', color: 'action.active' }} />
            <Typography sx={{ fontSize: '0.8125rem', flex: 1 }}>
              {category.name}
            </Typography>
            {status === 'new' && (
              <Chip label="New" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
            )}
            {status === 'updated' && (
              <Chip label="Updated" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
            )}
          </Box>
        }
      >
        {children.map(child => renderCategoryTree(child, buildChildren, level + 1))}
      </TreeItem>
    );
  };

  const renderMoodleContent = () => {
    // Special handling for categories - render as tree view
    if (contentType === 'categories') {
      const { rootCategories, buildChildren } = buildCategoryTree(moodleCategories);

      // Apply status filter to categories
      const filteredCategories = moodleCategories.filter((cat) => {
        if (statusFilter === 'all') return true;
        const status = getItemStatus(cat);
        if (statusFilter === 'new') return status === 'new';
        if (statusFilter === 'updated') return status === 'updated';
        return true;
      });

      // Only show root categories and their descendants if they pass the filter
      const filteredRootCategories = rootCategories.filter(root => {
        const hasMatchingDescendant = (catId: number): boolean => {
          if (filteredCategories.some(c => c.id === catId)) return true;
          const children = buildChildren(catId);
          return children.some(child => hasMatchingDescendant(child.id));
        };
        return hasMatchingDescendant(root.id);
      });

      return (
        <Box sx={{ p: 2, overflow: 'auto', maxHeight: '100%' }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                // Expand all categories
                setExpandedCategoryIds(moodleCategories.map(c => c.id.toString()));
              }}
            >
              Expand All
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setExpandedCategoryIds([])}
            >
              Collapse All
            </Button>
          </Box>
          <SimpleTreeView
            expandedItems={expandedCategoryIds}
            onExpandedItemsChange={(event, itemIds) => setExpandedCategoryIds(itemIds as string[])}
          >
            {filteredRootCategories.map(root => renderCategoryTree(root, buildChildren))}
          </SimpleTreeView>
        </Box>
      );
    }

    // Regular table rendering for other content types
    let items: Array<{ id: string; primary: string; secondary?: string; item: any }> = [];

    switch (contentType) {
      case 'courses':
        items = moodleCourses.map(c => ({
          id: c.id.toString(),
          primary: c.fullname,
          secondary: `${c.shortname} (ID: ${c.id})`,
          item: c,
        }));
        break;
      case 'competency_frameworks':
        items = moodleFrameworks.map(f => ({
          id: f.id.toString(),
          primary: f.shortname,
          secondary: f.idnumber ? `ID: ${f.idnumber}` : undefined,
          item: f,
        }));
        break;
      case 'learning_path_templates':
        items = moodleTemplates.map(t => ({
          id: t.id.toString(),
          primary: t.name,
          secondary: t.idnumber ? `ID: ${t.idnumber}` : undefined,
          item: t,
        }));
        break;
    }

    // Apply status filter
    const filteredItems = items.filter((item) => {
      if (statusFilter === 'all') return true;
      const status = getItemStatus(item.item);
      if (statusFilter === 'new') return status === 'new';
      if (statusFilter === 'updated') return status === 'updated';
      return true;
    });

    return (
      <TableContainer sx={{ maxHeight: '100%' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 48 }}>
                <Checkbox
                  size="small"
                  indeterminate={selectedItems.size > 0 && selectedItems.size < filteredItems.length}
                  checked={filteredItems.length > 0 && selectedItems.size === filteredItems.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Status</TableCell>
              {visibleColumns.id && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>ID</TableCell>}
              {visibleColumns.shortname && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Shortname</TableCell>}
              {visibleColumns.category && contentType === 'courses' && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Category</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.map((item) => {
              const status = getItemStatus(item.item);
              const course = item.item as MoodleCourse;

              // Count visible columns for CMI5 row colspan
              let colSpan = 3; // checkbox + name + status
              if (visibleColumns.id) colSpan++;
              if (visibleColumns.shortname) colSpan++;
              if (visibleColumns.category && contentType === 'courses') colSpan++;

              return (
                <React.Fragment key={item.id}>
                  <TableRow
                    hover
                    onClick={() => handleToggleItem(item.id)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedItems.has(item.id)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>
                      {item.primary}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {status === 'new' && (
                        <Chip label="New" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                      {status === 'updated' && (
                        <Chip label="Updated" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                      {status === 'duplicate' && (
                        <Chip label="Duplicate" size="small" color="default" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </TableCell>
                    {visibleColumns.id && <TableCell sx={{ fontSize: '0.75rem' }}>{course.id}</TableCell>}
                    {visibleColumns.shortname && <TableCell sx={{ fontSize: '0.75rem' }}>{course.shortname}</TableCell>}
                    {visibleColumns.category && contentType === 'courses' && (
                      <TableCell sx={{ fontSize: '0.75rem' }}>{course.category}</TableCell>
                    )}
                  </TableRow>

                  {/* CMI5 Activities Row - show for courses from both Moodle and S3 sources */}
                  {contentType === 'courses' && sourceType === 'moodle' && selectedMoodleSource && (
                    <CMI5ActivitiesRow
                      sourceId={selectedMoodleSource}
                      sourceType="moodle"
                      courseId={course.id}
                      courseName={course.fullname}
                      colSpan={colSpan}
                    />
                  )}
                  {contentType === 'courses' && sourceType === 's3' && selectedS3Environment && (
                    <CMI5ActivitiesRow
                      sourceId={selectedS3Environment}
                      sourceType="s3"
                      courseId={course.id}
                      courseName={course.fullname}
                      colSpan={colSpan}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderS3Content = () => {
    // Special handling for categories - render as tree view
    if (contentType === 'categories' && s3Categories.length > 0) {
      const { rootCategories, buildChildren } = buildCategoryTree(s3Categories);

      // Apply status filter to categories
      const filteredCategories = s3Categories.filter((cat) => {
        if (statusFilter === 'all') return true;
        const status = getItemStatus(cat);
        if (statusFilter === 'new') return status === 'new';
        if (statusFilter === 'updated') return status === 'updated';
        return true;
      });

      // Only show root categories and their descendants if they pass the filter
      const filteredRootCategories = rootCategories.filter(root => {
        const hasMatchingDescendant = (catId: number): boolean => {
          if (filteredCategories.some(c => c.id === catId)) return true;
          const children = buildChildren(catId);
          return children.some(child => hasMatchingDescendant(child.id));
        };
        return hasMatchingDescendant(root.id);
      });

      return (
        <Box sx={{ p: 2, overflow: 'auto', maxHeight: '100%' }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                // Expand all categories
                setExpandedCategoryIds(s3Categories.map(c => c.id.toString()));
              }}
            >
              Expand All
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setExpandedCategoryIds([])}
            >
              Collapse All
            </Button>
          </Box>
          <SimpleTreeView
            expandedItems={expandedCategoryIds}
            onExpandedItemsChange={(event, itemIds) => setExpandedCategoryIds(itemIds as string[])}
          >
            {filteredRootCategories.map(root => renderCategoryTree(root, buildChildren))}
          </SimpleTreeView>
        </Box>
      );
    }

    // Regular file listing for other content types
    // Apply status filter
    const filteredFiles = s3Files.filter((file) => {
      if (statusFilter === 'all') return true;
      const status = getItemStatus(file);
      if (statusFilter === 'new') return status === 'new';
      if (statusFilter === 'updated') return status === 'updated';
      return true;
    });

    return (
      <TableContainer sx={{ maxHeight: '100%' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 48 }}>
                <Checkbox
                  size="small"
                  indeterminate={selectedItems.size > 0 && selectedItems.size < filteredFiles.length}
                  checked={filteredFiles.length > 0 && selectedItems.size === filteredFiles.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Status</TableCell>
              {visibleColumns.shortname && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Shortname</TableCell>}
              {visibleColumns.category && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Category</TableCell>}
              {visibleColumns.size && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Size</TableCell>}
              {visibleColumns.date && <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Date</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFiles.map((file) => {
              const status = getItemStatus(file);

              // Calculate colspan for CMI5 row
              let colSpan = 3; // checkbox + name + status
              if (visibleColumns.shortname) colSpan++;
              if (visibleColumns.category) colSpan++;
              if (visibleColumns.size) colSpan++;
              if (visibleColumns.date) colSpan++;

              // Extract course ID from metadata for CMI5 activities
              // Check both camelCase (courseId) and lowercase (courseid) for compatibility
              const courseId = file.metadata?.courseId
                ? parseInt(file.metadata.courseId)
                : file.metadata?.courseid
                  ? parseInt(file.metadata.courseid)
                  : null;
              const courseName = file.metadata?.courseFullname || file.metadata?.coursefullname || file.name;

              return (
                <React.Fragment key={file.key}>
                  <TableRow
                    hover
                    onClick={() => handleToggleItem(file.key)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedItems.has(file.key)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip
                          title={`${getDisplayName(file)}${file.name !== getDisplayName(file) ? `\nFile: ${file.name}` : ''}\nKey: ${file.key}`}
                          placement="top"
                          arrow
                        >
                          <span>{getDisplayName(file)}</span>
                        </Tooltip>
                        {file.versionInfo && file.versionInfo.hasVersionHistory && (
                          <>
                            <Chip
                              label={`${file.versionInfo.currentVersion} (${file.versionInfo.totalVersions})`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                            {selectedVersions[file.key] ? (
                              <Chip
                                label={`Use ${selectedVersions[file.key]}`}
                                size="small"
                                color="success"
                                variant="filled"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                                onDelete={(e) => handleClearVersion(file.key, e)}
                                onClick={(e) => handleOpenVersionDialog(file, e)}
                              />
                            ) : (
                              <Chip
                                label="Select Version"
                                size="small"
                                color="default"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem', cursor: 'pointer' }}
                                onClick={(e) => handleOpenVersionDialog(file, e)}
                              />
                            )}
                          </>
                        )}
                        {file.versionInfo && file.versionInfo.currentVersion === 'legacy' && (
                          <Chip label="Legacy" size="small" color="default" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {status === 'new' && (
                        <Chip label="New" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                      {status === 'updated' && (
                        <Chip label="Updated" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                      {status === 'duplicate' && (
                        <Chip label="Duplicate" size="small" color="default" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </TableCell>
                    {visibleColumns.shortname && <TableCell sx={{ fontSize: '0.75rem' }}>{file.metadata?.courseShortname || file.metadata?.courseshortname || '-'}</TableCell>}
                    {visibleColumns.category && <TableCell sx={{ fontSize: '0.75rem' }}>{file.metadata?.courseCategory || file.metadata?.coursecategory || '-'}</TableCell>}
                    {visibleColumns.size && <TableCell sx={{ fontSize: '0.75rem' }}>{formatBytes(file.size)}</TableCell>}
                    {visibleColumns.date && <TableCell sx={{ fontSize: '0.75rem' }}>{formatDate(file.lastModified)}</TableCell>}
                  </TableRow>

                  {/* CMI5 Activities Row - show for courses from S3 */}
                  {contentType === 'courses' && courseId && selectedS3Environment && (
                    <CMI5ActivitiesRow
                      sourceId={selectedS3Environment}
                      sourceType="s3"
                      courseId={courseId}
                      courseName={courseName}
                      colSpan={colSpan}
                      courseKey={file.key}
                      selectedActivityVersions={selectedCmi5ActivityVersions[file.key] || {}}
                      onActivityVersionChange={handleActivityVersionChange}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
      {/* Compact Selectors Row */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={sourceType}
            label="Type"
            onChange={(e) => {
              setSourceType(e.target.value as SourceType);
              setSelectedItems(new Set());
            }}
          >
            <MenuItem value="s3">S3</MenuItem>
            <MenuItem value="moodle">Moodle</MenuItem>
          </Select>
        </FormControl>

        {/* Instance Selector */}
        {sourceType === 'moodle' ? (
          <FormControl size="small" sx={{ flex: 2 }}>
            <InputLabel>Instance</InputLabel>
            <Select
              value={selectedMoodleSource}
              label="Instance"
              onChange={(e) => setSelectedMoodleSource(e.target.value)}
            >
              {moodleSources.map((source) => (
                <MenuItem key={source.id} value={source.id}>
                  {source.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <FormControl size="small" sx={{ flex: 2 }}>
            <InputLabel>Environment</InputLabel>
            <Select
              value={selectedS3Environment}
              label="Environment"
              onChange={(e) => setSelectedS3Environment(e.target.value)}
            >
              {s3Environments.map((env) => (
                <MenuItem key={env.id} value={env.id}>
                  {env.name} ({env.bucketName})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Content Type Selector */}
      <FormControl size="small" fullWidth>
        <InputLabel>Content Type</InputLabel>
        <Select
          value={contentType}
          label="Content Type"
          onChange={(e) => {
            setContentType(e.target.value as ContentType);
            setSelectedItems(new Set());
          }}
        >
          <MenuItem value="courses">Courses</MenuItem>
          <MenuItem value="competency_frameworks">Competency Frameworks</MenuItem>
          <MenuItem value="learning_path_templates">Learning Path Templates</MenuItem>
          <MenuItem value="categories">Categories</MenuItem>
        </Select>
      </FormControl>

      {/* Status Filter Buttons */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button
          size="small"
          variant={statusFilter === 'all' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('all')}
          sx={{ flex: 1, minWidth: 0 }}
        >
          All
        </Button>
        <Button
          size="small"
          variant={statusFilter === 'new' ? 'contained' : 'outlined'}
          color="success"
          onClick={() => setStatusFilter('new')}
          sx={{ flex: 1, minWidth: 0 }}
        >
          New
        </Button>
        <Button
          size="small"
          variant={statusFilter === 'updated' ? 'contained' : 'outlined'}
          color="warning"
          onClick={() => setStatusFilter('updated')}
          sx={{ flex: 1, minWidth: 0 }}
        >
          Updated
        </Button>
      </Box>

      {/* Compact Selection Actions */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Button size="small" onClick={handleSelectAll}>All</Button>
        <Button size="small" onClick={handleClearSelection}>Clear</Button>
        <Button size="small" startIcon={<RefreshIcon />} onClick={sourceType === 'moodle' ? loadMoodleContent : loadS3Content} />
        <IconButton 
          size="small" 
          onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
          title="Show/Hide Columns"
        >
          <ViewColumnIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }} />
        {selectedItems.size > 0 && (
          <Chip label={`${selectedItems.size}`} size="small" color="primary" />
        )}
      </Box>

      {/* Column Visibility Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={() => setColumnMenuAnchor(null)}
      >
        <MenuItem onClick={() => setVisibleColumns(prev => ({ ...prev, id: !prev.id }))}>
          <Checkbox size="small" checked={visibleColumns.id} />
          <Typography variant="body2">ID</Typography>
        </MenuItem>
        <MenuItem onClick={() => setVisibleColumns(prev => ({ ...prev, shortname: !prev.shortname }))}>
          <Checkbox size="small" checked={visibleColumns.shortname} />
          <Typography variant="body2">Shortname</Typography>
        </MenuItem>
        <MenuItem onClick={() => setVisibleColumns(prev => ({ ...prev, category: !prev.category }))}>
          <Checkbox size="small" checked={visibleColumns.category} />
          <Typography variant="body2">Category</Typography>
        </MenuItem>
        <MenuItem onClick={() => setVisibleColumns(prev => ({ ...prev, size: !prev.size }))}>
          <Checkbox size="small" checked={visibleColumns.size} />
          <Typography variant="body2">Size</Typography>
        </MenuItem>
        <MenuItem onClick={() => setVisibleColumns(prev => ({ ...prev, date: !prev.date }))}>
          <Checkbox size="small" checked={visibleColumns.date} />
          <Typography variant="body2">Date</Typography>
        </MenuItem>
      </Menu>

      {/* Error Display */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Content List - takes remaining space */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, minHeight: 0 }}>
          {sourceType === 'moodle' ? renderMoodleContent() : renderS3Content()}
          {((sourceType === 'moodle' &&
            (contentType === 'courses' && moodleCourses.length === 0) ||
            (contentType === 'competency_frameworks' && moodleFrameworks.length === 0) ||
            (contentType === 'learning_path_templates' && moodleTemplates.length === 0) ||
            (contentType === 'categories' && moodleCategories.length === 0)) ||
            (sourceType === 's3' && s3Files.length === 0)) && !loading && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No content
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Version Selection Dialog */}
      <Dialog
        open={versionDialogOpen}
        onClose={() => setVersionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Select Version
          {versionDialogFile && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {getDisplayName(versionDialogFile)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {versionDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : versionDialogVersions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No version history available
            </Typography>
          ) : (
            <List>
              {versionDialogVersions.map((version) => (
                <ListItem key={version.version} disablePadding>
                  <ListItemButton onClick={() => handleSelectVersion(version.version)}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">{version.version}</Typography>
                          {version.version === versionDialogFile?.versionInfo?.currentVersion && (
                            <Chip label="Current" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" component="div">
                            Uploaded by: {version.uploadedBy || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" component="div">
                            {new Date(version.timestamp).toLocaleString()}
                          </Typography>
                          {version.description && (
                            <Typography variant="caption" component="div">
                              {version.description}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString();
}
